import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { get, merge } from 'lodash'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ExtendedTextField from '../materialui/ExtendedTextField'
import PasswordPolicyChecklist from './PasswordPolicyChecklist'
import { generatePassword } from '../../utils/passwordUtils'

const FormikPasswordField = ({
  formik,
  name,
  label,
  autoFocus,
  autoComplete,
  disabled,
  slotProps,
  size,
  placeholder,
  helperText: helperTextProp,
  passwordPolicy
}) => {
  const { formatMessage } = useIntl()
  const formikValue = get(formik.values, name)
  const [value, setValue] = useState(formikValue || '')
  const [showPassword, setShowPassword] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const hasError = get(formik.touched, name) && Boolean(get(formik.errors, name))

  useEffect(() => {
    setValue(formikValue || '')
  }, [formikValue])

  const helperText = useMemo(() => {
    if (hasError) {
      return get(formik.errors, name)
    }
    return helperTextProp || ''
  }, [formik, name, hasError, helperTextProp])

  const handleToggleVisibility = () => {
    setShowPassword((prev) => !prev)
  }

  const handleGeneratePassword = () => {
    const newPassword = generatePassword(passwordPolicy)
    setValue(newPassword)
    formik.setFieldValue(name, newPassword)
    setShowPassword(true)
  }

  const handleGenerateClick = () => {
    if (value) {
      setConfirmDialogOpen(true)
    } else {
      handleGeneratePassword()
    }
  }

  const handleConfirmGenerate = () => {
    setConfirmDialogOpen(false)
    handleGeneratePassword()
  }

  const handleCancelGenerate = () => {
    setConfirmDialogOpen(false)
  }

  return (
    <>
      <ExtendedTextField
        autoFocus={autoFocus}
        autoComplete={autoComplete || 'new-password'}
        fullWidth
        id={name}
        label={label}
        name={name}
        placeholder={placeholder}
        type={showPassword ? 'text' : 'password'}
        onChange={(event) => {
          const newValue = event.target.value
          setValue(newValue)
          formik.setFieldValue(name, newValue)
        }}
        onBlur={() => {
          formik.setFieldTouched(name, true)
        }}
        value={value}
        variant="outlined"
        error={hasError}
        helperText={helperText}
        disabled={disabled}
        slotProps={merge(
          {
            inputLabel: {
              shrink: Boolean(placeholder) || (value !== '' && value != null)
            }
          },
          slotProps
        )}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {passwordPolicy && (
                <Tooltip title={formatMessage({ id: 'passwordPolicy.generate' })}>
                  <IconButton
                    aria-label="generate password"
                    onClick={handleGenerateClick}
                    edge="end"
                    size="small"
                  >
                    <AutoFixHighIcon />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleToggleVisibility}
                edge="end"
                size="small"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
        size={size}
      />
      {passwordPolicy && (
        <PasswordPolicyChecklist value={value} policy={passwordPolicy} />
      )}
      <Dialog open={confirmDialogOpen} onClose={handleCancelGenerate}>
        <DialogTitle>
          <FormattedMessage id="passwordPolicy.confirmGenerate.title" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage id="passwordPolicy.confirmGenerate.message" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelGenerate}>
            <FormattedMessage id="app.actions.cancel.label" />
          </Button>
          <Button onClick={handleConfirmGenerate} variant="contained" autoFocus>
            <FormattedMessage id="app.actions.confirm.label" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default FormikPasswordField
